import { CategoricalDifferenceTester, NumericDifferenceTester } from "utils/stats";
import { Conditions, ConditionSetFactory, CategoricalConditionFactory, NumericConditionFactory } from "utils/insight/conditions";
import { ChartAxisType, ChartSeriesType } from "utils/insight/types";
import { DataProperties } from "utils/insight/properties";
export class DatasetMetadata {
    outcome;
    columns;
    constructor(outcome, columns) {
        this.outcome = outcome;
        this.columns = columns;
    }
    getOutcome() {
        return this.outcome;
    }
    getColumns() {
        return this.columns;
    }
    getColumn(id) {
        const column = this.columns[id];
        if (column === undefined) {
            throw new Error(`invalid id: ${id}`);
        }
        return column;
    }
    getConditionSets(id) {
        return this.getColumn(id).getConditionSets(id);
    }
    getConditionSetFactory() {
        const factoryMap = {};
        this.columns.forEach((column, index) => {
            factoryMap[column.getName()] = column.getConditionFactory(index);
        });
        return new ConditionSetFactory(factoryMap);
    }
    static create(outcome, columns) {
        return new DatasetMetadata(createOutcomeMetadata(outcome), columns.map(ColumnMetadata.create));
    }
}
export function humanize(x, n = 6) {
    return x.toFixed(n).replace(/\.?0*$/, "");
}
export class CountOutcomeMetadata {
    getName() {
        return "Count";
    }
    getLabel() {
        return "Count";
    }
    isBinary() {
        return false;
    }
    getUnit() {
        return "";
    }
    getDifferenceTester() {
        return NumericDifferenceTester.INSTANCE;
    }
    formatValue(x) {
        const value = x.getTotalN();
        return isFinite(value) ? Number(humanize(value)) : undefined;
    }
}
class TargetOutcomeMetadata {
    name;
    constructor(name) {
        this.name = name;
    }
    getName() {
        return this.name;
    }
    getLabel() {
        return this.name;
    }
    isBinary() {
        return false;
    }
    getUnit() {
        return "";
    }
    getDifferenceTester() {
        return NumericDifferenceTester.INSTANCE;
    }
    formatValue(x) {
        const value = x.getMean();
        return isFinite(value) ? Number(humanize(value)) : undefined;
    }
}
class BinaryOutcomeMetadata extends TargetOutcomeMetadata {
    success;
    constructor(name, success) {
        super(name);
        this.success = success;
    }
    isBinary() {
        return true;
    }
    getLabel() {
        return `${this.getName()}: ${this.getSuccessLabel()}`;
    }
    getSuccessLabel() {
        if (typeof this.success == "boolean") {
            return this.success ? "True" : "False";
        }
        return this.success.toString();
    }
    getUnit() {
        return "%";
    }
    getSuccessValue() {
        return this.success;
    }
    getDifferenceTester() {
        return CategoricalDifferenceTester.INSTANCE;
    }
    formatValue(x) {
        const value = x.getMean();
        return isFinite(value) ? Number(humanize(value * 100, 2)) : undefined;
    }
}
export function createOutcomeMetadata(raw) {
    if (raw.name === undefined) {
        return new CountOutcomeMetadata();
    }
    else if (raw.success_value === undefined) {
        return new TargetOutcomeMetadata(raw.name);
    }
    else {
        return new BinaryOutcomeMetadata(raw.name, raw.success_value);
    }
}
export function isBinaryOutcome(outcome) {
    return "success" in outcome;
}
export class ColumnMetadata {
    name;
    constructor(name) {
        this.name = name;
    }
    getName() {
        return this.name;
    }
    getConditionSets(id) {
        const sets = [];
        const n = this.getLevelCount();
        for (let i = 0; i < n; i++) {
            sets.push(Conditions.single(id, i));
        }
        return sets;
    }
    static create(raw) {
        switch (raw.type) {
            case "binned_numeric":
                return NumericColumnMetadata.create(raw);
            case "categorical":
            default:
                return CategoricalColumnMetadata.create(raw);
        }
    }
}
export class CategoricalColumnMetadata extends ColumnMetadata {
    values;
    constructor(name, values) {
        super(name);
        this.values = values;
    }
    getLevelCount() {
        return this.values.length;
    }
    getValueLabels() {
        return this.values;
    }
    getConditionFactory(id) {
        return CategoricalConditionFactory.create(id, this.values);
    }
    getDataProperty(value) {
        if (value >= 0 && value < this.values.length) {
            return DataProperties.value(this.getName(), this.values[value]);
        }
        throw new Error(`invalid value: ${value}`);
    }
    getChartAxisType() {
        return ChartAxisType.Category;
    }
    static create({ name, levels }) {
        const stringLevels = levels.map((l) => String(l));
        return new CategoricalColumnMetadata(name, stringLevels);
    }
}
export class NumericColumnMetadata extends ColumnMetadata {
    bins;
    hasMissing;
    constructor(name, bins, hasMissing) {
        super(name);
        this.bins = bins;
        this.hasMissing = hasMissing;
    }
    getLevelCount() {
        return this.bins.length - 1 + (this.hasMissing ? 1 : 0);
    }
    getValueLabels() {
        const labels = [];
        for (let i = 1; i < this.bins.length; i++) {
            const lower = this.bins[i - 1];
            const upper = this.bins[i];
            if (lower == upper) {
                labels.push(lower.toString());
            }
            else if (lower == Infinity) {
                labels.push(`<=${upper}`);
            }
            else if (i == 1) {
                labels.push(`${lower} to ${upper}`);
            }
            else if (upper == Infinity) {
                labels.push(`>${lower}`);
            }
            else {
                labels.push(`>${lower} to ${upper}`);
            }
        }
        if (this.hasMissing) {
            labels.push("{Missing}");
        }
        return labels;
    }
    getConditionFactory(id) {
        return new NumericConditionFactory(id, this.bins, this.hasMissing);
    }
    static countDecimals(x) {
        const split = x.toLocaleString("en-US", { maximumFractionDigits: 20 }).split(".");
        if (split[1] === undefined) {
            return 0;
        }
        return split[1].length;
    }
    getDataProperty(value) {
        if (value >= 0 && value < this.bins.length - 1) {
            const start = this.bins[value];
            const end = this.bins[value + 1];
            return DataProperties.interval(this.getName(), start, end);
        }
        else if (this.hasMissing && value == this.bins.length - 1) {
            return DataProperties.missing(this.getName());
        }
        throw new Error(`invalid value: ${value}`);
    }
    getChartAxisType() {
        return ChartAxisType.Category; //ChartAxisType.Number;
    }
    static create({ name, bins, has_missing }) {
        const numericBins = bins.map((x) => (typeof x == "number" ? x : parseFloat(x)));
        return new NumericColumnMetadata(name, numericBins, has_missing);
    }
}
export class LabelFactory {
    metadata;
    values;
    conditionFactories;
    constructor(metadata, values, conditionFactories) {
        this.metadata = metadata;
        this.values = values;
        this.conditionFactories = conditionFactories;
    }
    getOutcomeLabel() {
        return this.metadata.getOutcome().getLabel();
    }
    getUnit() {
        return this.metadata.getOutcome().getUnit();
    }
    formatOutcomeValue(x) {
        return this.metadata.getOutcome().formatValue(x);
    }
    getRawValues(conditions) {
        const values = [];
        for (const { id, value } of conditions) {
            values.push(this.getRawValue(id, value));
        }
        return values;
    }
    getRawValue(id, value) {
        if (this.getColumnChartAxisType(id) == ChartAxisType.Category) {
            return this.getValueLabel(id, value);
        }
        return this.conditionFactories[id].getRawValue(value);
    }
    overrideAxisTypeAndGetRawValue(id, value) {
        return this.conditionFactories[id].getRawValue(value);
    }
    getColumnChartAxisType(id) {
        return this.metadata.getColumn(id).getChartAxisType();
    }
    getColumnChartSeriesType(id) {
        switch (this.getColumnChartAxisType(id)) {
            case ChartAxisType.Number:
                return ChartSeriesType.Line;
            case ChartAxisType.Category:
            default:
                return ChartSeriesType.Bar;
        }
    }
    getSetLabel(conditions) {
        return conditions.map((c) => this.getConditionLabel(c)).join(" and ");
    }
    getOtherSetLabel(conditions) {
        return conditions.map((c) => this.getOtherConditionLabel(c)).join(" and ");
    }
    getSetColumnLabels(conditions) {
        return conditions.map((c) => this.getColumnLabel(c.id)).join(" and ");
    }
    getConditionLabel(condition) {
        const { id, value } = condition;
        return `${this.getColumnLabel(id)} is ${this.getValueLabel(id, value)}`;
    }
    getOtherConditionLabel(condition) {
        const { id, value } = condition;
        const n = this.metadata.getColumn(id).getLevelCount();
        if (this.metadata.getColumn(id).getLevelCount() > 2) {
            return `all other values of ${this.getColumnLabel(id)}`;
        }
        return this.getConditionLabel(Conditions.create(id, n - 1 - value));
    }
    getColumnLabel(id) {
        const columnLabel = this.metadata.getColumn(id).getName();
        if (columnLabel === undefined) {
            throw new Error("unknown column id");
        }
        return columnLabel;
    }
    getValueLabel(id, value) {
        const columnValues = this.values[id];
        if (columnValues === undefined) {
            throw new Error("unknown column id");
        }
        const valueLabel = columnValues[value];
        if (valueLabel === undefined) {
            throw new Error("unknown column value");
        }
        return valueLabel;
    }
    getValueLabels(conditions) {
        return conditions.map(({ id, value }) => this.getValueLabel(id, value)).join(" and ");
    }
    static create(metadata) {
        const values = metadata.getColumns().map((c) => c.getValueLabels());
        const conditionFactories = metadata.getColumns().map((c, id) => c.getConditionFactory(id));
        return new LabelFactory(metadata, values, conditionFactories);
    }
}
export class InsightDataPropertyFactory {
    metadata;
    constructor(metadata) {
        this.metadata = metadata;
    }
    getInsightDataProperties({ ids, conditions }) {
        const properties = [];
        if (ids !== undefined) {
            for (const id of ids) {
                properties.push(this.getColumnDataProperty(id));
            }
        }
        if (conditions !== undefined) {
            properties.push(...this.getDataProperties(conditions));
        }
        return properties;
    }
    getDataProperties(conditions) {
        const properties = [];
        for (const condition of conditions) {
            properties.push(this.getDataProperty(condition));
        }
        return properties;
    }
    getDataProperty({ id, value }) {
        return this.metadata.getColumn(id).getDataProperty(value);
    }
    getColumnDataProperty(id) {
        return DataProperties.column(this.metadata.getColumn(id).getName());
    }
}
